import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { queryClient } from 'index';
import { createProductGroup, getProductGroup, updateProductGroup, ProductGroup } from 'services/api.service';
import SplitRow, { SplitRowValue } from './SplitRow';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  groupId?: string; // present = edit
}

const MAX = 90;

export default function GroupFormModal({ isOpen, onClose, groupId }: Props) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [applyCashSplit, setApplyCashSplit] = useState(false);
  const [rows, setRows] = useState<SplitRowValue[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    if (!groupId) {
      setName('');
      setApplyCashSplit(false);
      setRows([]);
      return;
    }
    getProductGroup(groupId).then((g: ProductGroup) => {
      setName(g.name);
      setApplyCashSplit(g.applyCashSplit);
      setRows(
        g.splits.map((s) => ({
          lookup: '',
          receiverCustomerId: s.receiverCustomerId,
          receiverName: '(recebedor salvo)',
          percentage: Number(s.percentage),
        })),
      );
    }).catch((err: any) => {
      toast({
        title: 'Erro',
        description: err?.response?.data?.message || 'Erro ao carregar grupo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, groupId]);

  const total = rows.reduce((acc, r) => acc + (Number(r.percentage) || 0), 0);

  const patchRow = (i: number, patch: Partial<SplitRowValue>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((prev) => [...prev, { lookup: '', receiverCustomerId: '', receiverName: '', percentage: '' }]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) {
      toast({ title: 'Informe o nome do grupo', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    for (const r of rows) {
      if (!r.receiverCustomerId) {
        toast({
          title: 'Recebedor não verificado',
          description: 'Verifique todos os recebedores',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (!(Number(r.percentage) > 0)) {
        toast({
          title: 'Percentual inválido',
          description: 'Cada recebedor precisa de um % maior que zero',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
    }
    const ids = rows.map((r) => r.receiverCustomerId);
    if (new Set(ids).size !== ids.length) {
      toast({
        title: 'Recebedor duplicado',
        description: 'Cada recebedor só pode aparecer uma vez',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (total > MAX) {
      toast({ title: `Soma dos splits acima de ${MAX}%`, status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    const splits = rows.map((r) => ({ receiverCustomerId: r.receiverCustomerId, percentage: Number(r.percentage) }));
    setLoading(true);
    try {
      if (groupId) {
        await updateProductGroup(groupId, { name, applyCashSplit, splits });
      } else {
        await createProductGroup({ name, applyCashSplit, splits });
      }
      await queryClient.invalidateQueries({ queryKey: ['productGroups'] });
      toast({ title: 'Grupo salvo', status: 'success', duration: 4000, isClosable: true });
      onClose();
    } catch (err: any) {
      toast({
        title: 'Erro',
        description: err?.response?.data?.message || 'Erro ao salvar grupo',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{groupId ? 'Editar grupo' : 'Novo grupo'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Nome *</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Bebidas" />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <Switch isChecked={applyCashSplit} onChange={(e) => setApplyCashSplit(e.target.checked)} mr={2} />
              <FormLabel mb={0}>Aplicar split em vendas de dinheiro</FormLabel>
            </FormControl>
            <VStack spacing={2} align="stretch">
              <Text fontWeight="bold">Recebedores do split</Text>
              {rows.map((r, i) => (
                <SplitRow key={i} value={r} onChange={(patch) => patchRow(i, patch)} onRemove={() => removeRow(i)} />
              ))}
              <Button size="sm" variant="outline" onClick={addRow}>
                + Adicionar recebedor
              </Button>
            </VStack>
            <VStack spacing={1} align="stretch">
              <Text fontSize="sm">
                Total: {total}% de {MAX}%
              </Text>
              <Progress value={(total / MAX) * 100} colorScheme={total > MAX ? 'red' : 'green'} borderRadius="md" />
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="green" isLoading={loading} onClick={save}>
              Salvar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
